import {Injectable} from '@angular/core';
import {Observable} from "rxjs";
import {VetineoApiResponse} from "../models/vetineo-api-response.model";
import {map, take} from "rxjs/operators";
import {ApiService} from "./api.service";
import {CommonUtils} from "../utils/common";
import {DataGridServiceInterface} from "./data-grid-service.interface";
import {VetDoctorVetVisitTypeDgSearchResults} from "../models/datagrid-search-results/vet-doctor-vet-visit-type.dg-search-results.model";
import {VetDoctorVetVisitType} from "../models/vet-doctor-vet-visit-type.model";
import {ReservationsCheck} from "../models/reservations-check.model";

/**
 * Serwis warstwy danych zarządzający relacjami (mapowaniem) pomiędzy
 * lekarzami weterynarii a przypisanymi do nich typami wizyt.
 * Umożliwia indywidualną konfigurację (np. nadpisywanie domyślnego czasu
 * trwania wizyty dla konkretnego specjalisty) oraz masowe przypisywanie uprawnień.
 */
@Injectable({
  providedIn: 'root'
})
export class VetDoctorVetVisitTypeService implements DataGridServiceInterface<Observable<VetDoctorVetVisitTypeDgSearchResults>> {

  constructor(private apiService: ApiService, public commonUtils: CommonUtils) {
  }

  /**
   * Pobiera paginowaną listę mapowań (lekarz <-> typ wizyty) dla komponentu DataGrid.
   *
   * @param limit Maksymalna liczba rekordów.
   * @param offset Przesunięcie względem początku zestawu danych.
   * @param sortOrder Kierunek sortowania ('ASC' / 'DESC').
   * @param orderBy Kolumna sortująca.
   * @param showDeletedObjects Flaga uwzględniania usuniętych/nieaktywnych przypisań.
   * @returns Observable emitujący ustandaryzowany obiekt wyników dla tabeli.
   */
  getResults(limit: number, offset: number, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<VetDoctorVetVisitTypeDgSearchResults> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/vetdoctor-vet-visit-types/${showDeletedObjects}/${limit}/${offset}/${sortOrder}/${orderBy}`, {}).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return new VetDoctorVetVisitTypeDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new VetDoctorVetVisitType(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);

      } else {
        return undefined;
      }
    }));
  }

  /**
   * Filtruje i wyszukuje konkretne powiązania lekarzy z typami wizyt.
   *
   * @param limit Rozmiar strony.
   * @param offset Przesunięcie.
   * @param searchFieldsData Mapa zawierająca zdefiniowane filtry wyszukiwania.
   * @param sortOrder Kierunek sortowania.
   * @param orderBy Kolumna sortująca.
   * @param showDeletedObjects Flaga widoczności usuniętych rekordów.
   * @returns Observable emitujący przefiltrowaną listę dla DataGrid.
   */
  searchForResults(limit: number, offset: number, searchFieldsData: Map<string, string>, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<VetDoctorVetVisitTypeDgSearchResults> {
    const data = {
      limit: limit,
      offset: offset,
      searchFieldsData: this.commonUtils.mapToSearchFieldList(searchFieldsData),
      sortOrder: sortOrder,
      orderBy: orderBy,
      showDeletedObjects: showDeletedObjects
    }
    return this.apiService.post<VetineoApiResponse>(`/dashboard/vetdoctor-vet-visit-types/search`, data).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        console.log(vetineoApiResponse.response.results);
        return new VetDoctorVetVisitTypeDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new VetDoctorVetVisitType(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera szczegóły pojedynczego mapowania na podstawie jego unikalnego identyfikatora.
   *
   * @param uuid Unikalny identyfikator mapowania (relacji).
   * @returns Observable emitujący obiekt szczegółów powiązania.
   */
  getByUUID(uuid: string): Observable<VetDoctorVetVisitType> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/vetdoctor-vet-visit-types/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        console.log(vetineoApiResponse.response)
        return this.commonUtils.bestTypedCopyEver<VetDoctorVetVisitType>(new VetDoctorVetVisitType(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Zapisuje nowe powiązanie pomiędzy wybranym lekarzem a typem wizyty.
   *
   * @param vetDoctorVetVisitType Obiekt transferowy zawierający parametry relacji.
   * @returns Observable emitujący utworzony obiekt przypisania.
   */
  addVetDoctorVetVisitType(vetDoctorVetVisitType: VetDoctorVetVisitType) {
    return this.apiService.post<VetineoApiResponse>('/dashboard/vetdoctor-vet-visit-types', vetDoctorVetVisitType).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        console.log(vetineoApiResponse.response)
        return this.commonUtils.bestTypedCopyEver<VetDoctorVetVisitType>(new VetDoctorVetVisitType(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Aktualizuje istniejące przypisanie. Używane np. wtedy, gdy lekarz decyduje się
   * zmienić swój domyślny czas trwania danej procedury (np. ze standardowych 15 minut na 30 minut).
   *
   * @param vetDoctorVetVisitType Zmodyfikowany obiekt relacji.
   * @returns Observable emitujący zaktualizowane przypisanie.
   */
  updateVetDoctorVetVisitType(vetDoctorVetVisitType: VetDoctorVetVisitType): Observable<VetDoctorVetVisitType> {
    return this.apiService.put<VetineoApiResponse>('/dashboard/vetdoctor-vet-visit-types', vetDoctorVetVisitType).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        console.log(vetineoApiResponse.response)
        return this.commonUtils.bestTypedCopyEver<VetDoctorVetVisitType>(new VetDoctorVetVisitType(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Usuwa powiązanie między lekarzem a typem wizyty.
   *
   * @param uuid Identyfikator mapowania przeznaczonego do usunięcia.
   * @returns Observable potwierdzający usunięcie statusem 'ok'.
   */
  deleteVisitType(uuid: string) {
    return this.apiService.delete<VetineoApiResponse>(`/dashboard/vetdoctor-vet-visit-types/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return 'ok';
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera wszystkie uprawnienia lekarzy przypisane do określonego zestawu typów wizyt (Bundle).
   *
   * @param uuid Unikalny identyfikator nadrzędnego zestawu (Bundle).
   * @param vetcodeId Identyfikator kliniki.
   * @returns Observable emitujący tablicę powiązań wchodzących w skład zestawu.
   */
  getByBundleUUID(uuid: string, vetcodeId): Observable<VetDoctorVetVisitType[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/vetdoctor-vet-visit-types/by-bundle/${uuid}/${vetcodeId}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new VetDoctorVetVisitType(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Masowo przypisuje uprawnienia wybranej grupie lekarzy do całego pakietu typów wizyt.
   *
   * @param vetineoVetVisitTypeBundleUUID Identyfikator pakietu typów wizyt.
   * @param uuids Tablica identyfikatorów lekarzy (VetDoctor UUIDs).
   * @param vetcodeId Identyfikator kliniki.
   * @returns Observable emitujący tablicę nowo utworzonych powiązań.
   */
  addDoctorsToBundle(vetineoVetVisitTypeBundleUUID: string, uuids: string[], vetcodeId): Observable<VetDoctorVetVisitType[]> {
    return this.apiService.post<VetineoApiResponse>('/dashboard/vetdoctor-vet-visit-types/multiples', {
      'bundleUUID': vetineoVetVisitTypeBundleUUID,
      'doctorsUUIDs': uuids,
      'vetcodeId':vetcodeId
    }).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new VetDoctorVetVisitType(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Weryfikuje mechanizmy zabezpieczające (Safety Check) przed usunięciem uprawnień lekarza
   * do danego typu wizyty. Sprawdza, czy w systemie nie widnieją już zaplanowane, przyszłe
   * rezerwacje tego typu przypisane do tego specjalisty.
   *
   * @param uuid Identyfikator mapowania (lekarz <-> wizyta).
   * @returns Observable emitujący model weryfikacji rezerwacji.
   */
  checkForReservationsForVetDoctorVisitType(uuid: any): Observable<ReservationsCheck> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/exist-objects-for-vet-doctor-visit-type/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<ReservationsCheck>(new ReservationsCheck(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }
}
